/**
 * "Results" — what providers have actually reported back, and what it did to
 * this campaign's credits. Slice 6.
 *
 * `CampaignProgressPanel`, above this on the page, answers "what happened to
 * each *person*" from `campaign_recipients`. This answers a related but
 * different question — "what happened to each *message*, and to the credit it
 * held" — from `messages`, `delivery_events` and `credit_ledger`, via
 * `GET /campaigns/:id/results`. The two do not add up to the same total and
 * must not be forced to: a skipped recipient never became a message, and an
 * `unknown` message has no settled recipient status.
 *
 * **Three things this panel exists to get right, because they are the three a
 * credit system gets asked about:**
 *
 *   1. **Accepted is not delivered.** `reading.accepted` (`sent + delivered +
 *      bounced` — what the provider actually took) and `messages.delivered`
 *      are shown as two different figures, never folded into one "sent".
 *   2. **A bounce did not consume a credit, said before anyone asks.** The
 *      credits section states outright that every released reservation cost
 *      nothing, and the number of releases is the number of bounces,
 *      rejections and rendering failures — never subtracted from consumed.
 *   3. **These are credits, not money.** Nothing here prints "spend", "cost",
 *      "charge", "price" or a currency symbol.
 *
 * Every figure is `campaignResultsReading`'s or the API's own — imported from
 * `@wend/shared`, never re-derived here, so this screen and a future admin
 * screen reading the same numbers cannot disagree about what "accepted" or
 * "still reserved" means.
 *
 * It keeps its own `reload`, independent of the campaign-wide poll
 * (`use-campaign-progress.ts`): that poll stops the instant a campaign reaches
 * a terminal status, but a bounce or a complaint can arrive from Amazon well
 * after this campaign itself says "Sent" — so this section is never left with
 * no way to ask again.
 *
 * **Slice 10 added the fourth thing: why the ones that did not arrive did not.**
 * `CampaignNotDelivered`, rendered below the bounce counters, splits the five
 * outcomes SES can report into five rows that each say whether the reader can
 * do anything about it. The counters above it stay: they split the bounced
 * messages three ways and have to add up to `messages.bounced`, while those
 * five answer "why did it not arrive" and include two outcomes — a rejection
 * and a rendering failure — that never became a bounce at all. Both count
 * messages rather than provider notifications, and neither totals: a message
 * SES delayed and then hard-bounced is truthfully on two of the five lines.
 *
 * **What slice 10 deliberately did not add here: a second skip breakdown.**
 * "Reached fewer people than the segment holds" is a question about *people*,
 * and `CampaignProgressPanel` above already answers it with every reason, the
 * contract's own sentence for each and a link to exactly who — restating it
 * here would give one screen two accounts of the same fact that can drift
 * apart. What this panel adds instead is the single line that connects the two
 * counts, because the gap between "526 people" and "5 accepted" is the first
 * thing a person notices and the skip breakdown is where the answer is.
 *
 * **And what nothing here shows, still: an open rate, a click rate or a read
 * receipt.** The reason has changed and the rule has not, so both are worth
 * writing down.
 *
 * Until slice 11 the reason was that the numbers did not exist: there was no
 * tracking pixel and no link rewriting anywhere in the product, and no WhatsApp
 * status webhook. That is no longer true for email. A workspace that switches
 * tracking on gets a configuration set, an event destination carrying OPEN and
 * CLICK, and rows in `delivery_events` for both. **The numbers can now exist,
 * for some workspaces, some of the time** — which makes the old sentence one of
 * the false promises slice 11 had to go and find.
 *
 * They are still not on this panel, and the argument is the one this file makes
 * everywhere else. An open rate is *directional*: image-blocking clients hide
 * real opens while Apple Mail Privacy Protection counts opens for people who
 * never looked, so it is wrong in both directions at once and cannot be read the
 * way the bounce rate beside it is read. Putting it in this grid, at the visual
 * rank of figures that are exact, would say it is the same kind of number. It
 * lives on `GET /overview/engagement` instead, with `ENGAGEMENT_CAVEAT_MESSAGES`
 * attached to it and `confidence: 'directional'` in its own shape — a type that
 * is deliberately not assignable to the KPI shape the tiles here are built from.
 *
 * And for a workspace with tracking off — the default, and the state most of
 * them are in — the numbers genuinely do not exist, which is the older argument
 * still standing: a "0%" reads as "nobody opened it" when the truth is "we do
 * not measure this".
 *
 * **Slice 12 makes this panel two panels, because the two channels no longer
 * report the same kind of thing.** The sentence above used to end "WhatsApp read
 * counts are still nothing but absent, and arrive with slice 12 or not at all."
 * They arrived. Meta's status webhook writes `sent` / `delivered` / `read` /
 * `failed` rows against `provider = 'meta_whatsapp'`, so a WhatsApp campaign now
 * has delivery figures of its own — and they are not the email ones with a
 * different logo on them:
 *
 *   - **there is no bounce on WhatsApp** (decision 1). Meta reports `failed` and
 *     an error code, and the code is the whole diagnosis: an unregistered
 *     number, a blown rate limit, a paused template and an expired 24-hour
 *     window all arrive as the same word. The three bounce counters, the two AWS
 *     rate warnings and `CampaignNotDelivered` are AWS's vocabulary end to end,
 *     so they are gated on `isBounceMeasured` — not on `isDeliveryMeasured`,
 *     which is now true on both channels. `CampaignWhatsAppDelivery` is what
 *     stands in their place, with `CampaignWaFailures` splitting `failed` seven
 *     ways;
 *   - **`accepted` is arithmetic that does not survive the crossing.**
 *     `campaignResultsReading` computes it as `sent + delivered + bounced` and
 *     excludes `failed` on the stated grounds that "the provider never took it"
 *     — which is exactly right for SES, where a `failed` message is one SES
 *     refused at the API call and which therefore never entered the mail system.
 *     On WhatsApp a `failed` message is one the Graph API *accepted*, handed back
 *     a `wamid` for, and only later reported as undelivered. Reusing that
 *     denominator would quietly drop every failed WhatsApp message out of
 *     "accepted", making a campaign where everything failed report nothing
 *     accepted at all. So the accepted and delivered figures at the top of this
 *     panel come from `whatsapp.accepted` / `whatsapp.delivered` when the
 *     WhatsApp half is present — see {@link ResultsBody} — and from the
 *     shared reading otherwise;
 *   - **a read figure is a floor and never a plain percentage.** That rule is
 *     enforced in `CampaignWhatsAppDelivery`, which is also where the argument
 *     for it is written down. The type carries it: `WaReadRate` is not
 *     assignable to `EngagementRate` or to `OverviewRateKpi`, so the component
 *     that says "approximate, in both directions" cannot be handed a number
 *     whose true value is only ever higher.
 *
 * The credits section is shared and stays shared, because the ledger rule is the
 * same shape on both channels — consume on delivery, release on the failure —
 * and only the word for the failure differs. What that costs is one channel-aware
 * sentence rather than a second copy of the section.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * SLICE 17: WHAT CAME OFF THIS PANEL, AND THE ONE THING THAT WENT ONTO IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Everything above this line is why the panel is shaped as it is, and until this
 * slice a good deal of it was also *on* the panel. The card description opened by
 * explaining that nothing is stored and that a replayed webhook therefore cannot
 * corrupt these figures; the credits paragraph restated its own rule twice; the
 * bounce definitions ran to six lines of prose. None of that changes what a
 * reader would conclude, which is the test slice 17 applies, and the reader it
 * was written for — whoever picked up the next slice — is reading this header
 * instead.
 *
 * **Two things were made louder rather than shorter, and both are the first
 * kind of claim this file exists to protect.**
 *
 * *Accepted is not delivered.* The two figures were already two figures, which
 * is the structural half; what they did not do was say it. "Accepted" reads as
 * an outcome to anybody who has not run a mail server, and the caption under it
 * described the arithmetic — `sent + delivered + bounced` — which is a
 * definition, not a warning. Both captions now end in the contrast itself, and
 * the delivered one leads with "confirmed delivered" before its percentage.
 *
 * *Hard against soft.* Three definitions in a `<dl>`, each against the counter
 * it defines, replacing a paragraph in which the two lines that had to be
 * contrasted were the second and third sentences of six. What each bounce means
 * *for the contact* — suppressed and finished, or fine and worth trying again —
 * is the half of each definition that decides whether somebody deletes a working
 * address, so it is the half that survived.
 *
 * **And one state that was being reported as its opposite.** `accepted === 0`
 * was a single sentence saying the provider had not accepted anything yet, so
 * there was nothing to report — which is true of a campaign that has not
 * started, and false of every other way to arrive at zero. A campaign SES
 * refused *every* message of has `messages.failed` set and `accepted` still
 * zero, because a `failed` email message never entered the mail system at all;
 * it rendered as "nothing to report" with the refusals nowhere on the panel. A
 * send still in flight has `queued` set and was told the same thing, which
 * invites somebody to schedule it a second time.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * THE ZERO-ACCEPTED BRANCH, AND WHY ITS FIRST FIX WAS THE SAME MISTAKE AGAIN
 * ────────────────────────────────────────────────────────────────────────────
 *
 * That fix made the one sentence three, chained on `failed` then `queued`, under
 * a comment asserting there were exactly three facts. **There are four, and they
 * are not exclusive**, which is the part a ternary cannot express. `accepted ===
 * 0` is not a state at all; it is the arithmetic outcome of counters that move
 * independently, so the branch reports every one that is set rather than
 * choosing between them. Three defects came out of treating it as a state, and
 * all three are the zero-versus-not-measured rule on the screen where it costs
 * most:
 *
 *   - **`unknown` was not read.** It is `messages.unknown` —
 *     "provider accepted, we never recorded it" — and `campaignResultsReading`
 *     leaves it out of `accepted` because *we cannot say whether the provider
 *     took it*, which is a reason to say so rather than a licence to assert the
 *     opposite. A campaign whose 400 messages SES accepted and whose write then
 *     failed fell through every arm and read "Nothing sent yet". The words for
 *     it already existed on this panel and sat in the `accepted > 0` return,
 *     unreachable in the one state that needed them; they are
 *     {@link AcceptedButNeverConfirmed} now and both branches render the same
 *     component.
 *   - **Refused beat queued.** A campaign SES refused ten messages of and which
 *     then paused mid-run for credit has both counters set — the runner leaves
 *     the remaining rows `queued`, unreserved, deliberately — and was told it
 *     was refused and finished while messages to real addresses were still to go
 *     out.
 *   - **"Refused" is an email word.** `CHANNEL_PROVIDER_LABEL[channel]` was
 *     substituted into a sentence whose claim is only true of SES. Meta's
 *     `failed` normally means the opposite — accepted, `wamid` returned, later
 *     reported undelivered — and the one case where it does not is when the
 *     WhatsApp half is present, because `whatsapp.accepted === 0` then proves
 *     every failure was pre-acceptance. With that half absent this panel cannot
 *     tell, and the branch says so instead of guessing.
 *
 * **The delivered caption is the same rule one line higher up.** It printed
 * `delivered / accepted` unconditionally, so a campaign accepted an hour ago
 * whose events have not landed read "Confirmed delivered — 0.0% of accepted"
 * under a zero. Nothing had failed. It now says "none yet" while every accepted
 * message is still awaiting its event, and gives counts rather than a percentage
 * below `RATE_SIGNIFICANCE_MINIMUM` — the rule the bounce block below has
 * carried since slice 6, finally applied to the figure a reader looks at first.
 *
 * **Slice 19: when nothing at all has come back, the panel says whether anything
 * ever has.** A campaign whose every accepted message still awaits its event
 * used to get one faint line — the provider "has not answered, not lost them" —
 * which is exactly right an hour after a send and exactly wrong on the live
 * deployment, where no report had ever reached Wend for any campaign. The
 * response now carries the workspace's `deliveryReports`, and
 * `unreportedExplanation` — the Overview's own wording — says *never*, flatly,
 * with the first missing step (and a link, where Integrations is the fix); or
 * that reports have arrived, naming what else the silence can be — delayed mail,
 * mail sent before reports were set up, or reports that stopped. The faint line
 * stays for the ordinary case: some reports back, some still to come.
 *
 * **And under that sentence, no counter at zero.** It says nothing can be told
 * about bounces or complaints, and the bounce counters used to follow it at
 * "Hard bounces 0, Soft bounces 0, Complaints 0 — the counts above are exact"
 * (review). In that state the failure sections show only what was reported: a
 * complaint count, and any reason a message did not arrive.
 */
import {
  AWS_BOUNCE_RATE_REVIEW,
  AWS_COMPLAINT_RATE_REVIEW,
  RATE_SIGNIFICANCE_MINIMUM,
  campaignResultsReading,
  isBounceMeasured,
  onlyBouncesReported,
  type Campaign,
  type CampaignResults,
  type CampaignResultsReading,
  type Channel,
  type DeliveryReports,
  type ProjectedFigures,
  type WaCampaignDelivery,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Kicker,
  MetricPanel,
  Spinner,
  StateChip,
} from '@wend/ui';
import { ListFilter, RotateCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { fetchCampaignResults } from '../../lib/api/campaigns';
import {
  CHANNEL_PROVIDER_LABEL,
  CHANNEL_PROVIDER_SHORT_LABEL,
  messageStatusMeaning,
} from '../../lib/campaign-display';
import { unreportedExplanation } from '../../lib/delivery-reports-display';
import { ProjectedFigureRow } from '../../components/ProjectedFigures';
import { formatCount, formatDateTime, pluralise } from '../../lib/format';
import { integrationTabPath } from '../integrations/integration-tab-url';
import { useAsyncData } from '../../lib/use-async-data';
import { Counter, SesFailureSections, formatRate } from './CampaignFailureSections';
import { CampaignNotDelivered } from './CampaignNotDelivered';
import { CampaignWhatsAppDelivery } from './CampaignWhatsAppDelivery';

export interface CampaignResultsPanelProps {
  campaign: Campaign;
  /** Changes whenever the recipient-side counts move, so a send in progress refetches this too. */
  refreshKey: string;
  /**
   * Focuses the recipient list on the people who never became a message.
   *
   * Optional because this panel is legible without it — the line it belongs to
   * says the same thing in words, and the reasons are broken down in the panel
   * above either way. It is a shortcut from "why so few?" to "these ones",
   * not the answer itself.
   */
  onShowSkipped?: () => void;
}

export function CampaignResultsPanel({
  campaign,
  refreshKey,
  onShowSkipped,
}: CampaignResultsPanelProps) {
  const { data, error, loadingFirst, refreshing, reload } = useAsyncData(
    (signal) => fetchCampaignResults(campaign.id, signal),
    [campaign.id, refreshKey],
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>Results</CardTitle>
          {refreshing ? (
            <Spinner size="sm" label="Updating the results" />
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={reload}>
              <RotateCw aria-hidden="true" className="size-4" />
              Refresh
            </Button>
          )}
        </div>
        <CardDescription>
          What {CHANNEL_PROVIDER_LABEL[campaign.channel]} has reported back.{' '}
          {campaign.channel === 'whatsapp'
            ? 'A delivery, a failure or a read receipt'
            : 'A delivery or a bounce'}{' '}
          can land hours after this campaign itself finishes, so this section keeps its own Refresh.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadingFirst ? (
          <div className="flex items-center gap-3 py-8 text-[0.92rem] text-muted">
            <Spinner size="md" label={null} />
            <span role="status">Loading the results…</span>
          </div>
        ) : data === null ? (
          <ErrorState error={error} title="Could not load the results" onRetry={reload} />
        ) : (
          <ResultsBody
            results={data.results}
            // The results are the same shape on both channels, but what an
            // empty delivery breakdown MEANS is not — see `CampaignNotDelivered`.
            channel={campaign.channel}
            // Null on every email campaign, by contract. Null on a WhatsApp
            // campaign only when the API predates slice 12's results reader —
            // which is a different statement from "nothing has come back yet"
            // and gets a different sentence rather than a screen full of zeros.
            whatsapp={data.whatsapp}
            // The workspace's, not this campaign's: whether any report has ever
            // reached Wend. Null on an API older than slice 19.
            deliveryReports={data.deliveryReports}
            // Slice 28. Non-null only where this workspace has never had a
            // report of any kind, this campaign has no terminal outcome of its
            // own, and the channel is email.
            projection={data.projection}
            onShowSkipped={onShowSkipped}
          />
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Nothing reported, and whether anything ever has been                        */
/* -------------------------------------------------------------------------- */

/**
 * Every accepted message of this campaign still awaits its event. The words are
 * `unreportedExplanation`'s — the Overview's — so the dashboard and a campaign
 * cannot tell the same workspace two different things about its reports.
 *
 * `warn` weight only when Wend knows none has ever arrived: that is a setup fact
 * a campaign is sent blind under. Received-before and not-known are callouts at
 * `info`, because either can be minutes of ordinary waiting.
 */
function UnreportedCampaign({
  channel,
  deliveryReports,
  complaints,
}: {
  channel: Channel;
  deliveryReports: DeliveryReports | null;
  /** This campaign's spam complaints — a report, which changes the sentence. */
  complaints: number;
}) {
  const explanation = unreportedExplanation(channel, deliveryReports, complaints);

  /*
   * SLICE 28: A STRIP, NOT A HUNDRED AND FOUR WORDS.
   *
   * This was the largest block on the campaign page and the owner quoted it
   * back. It printed `explanation.paragraphs` in full: a warn Callout naming an
   * environment variable (`API_BASE_URL`), an SNS topic and a webhook address,
   * on a screen a marketer opens to find out how their campaign did — and it
   * printed the same workspace-level fact again on every campaign they open.
   *
   * What a sender can act on is one sentence and one link, which is what is left.
   * Nothing is lost: the paragraphs are the workspace's fact, they are rendered
   * in full on the Overview's own disclosure, and the screen that repairs them is
   * one click away from here.
   *
   * The same shape as the Overview's `SetUpDeliveryReports`, deliberately — a
   * reader who learns this strip once reads it on both screens.
   */
  return (
    <div
      data-state="campaign-unreported"
      data-reports={explanation.state}
      data-role="campaign-reports-setup"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-base border border-warn/40 bg-warn/[0.04] px-4 py-3"
    >
      <StateChip
        state={explanation.state === 'never' ? 'not_set_up' : 'not_measured'}
        label={explanation.state === 'never' ? 'Delivery reports not set up' : 'No reports yet'}
      />
      <p className="min-w-0 flex-1 text-[0.82rem] leading-snug text-muted">
        No delivery figure for this campaign until {channel === 'whatsapp' ? 'Meta' : 'Amazon'}{' '}
        reports back.
      </p>
      {explanation.pointsAtSetup && (
        <Link
          to={integrationTabPath('email')}
          className="shrink-0 text-[0.82rem] text-text underline underline-offset-2"
        >
          Set up delivery reports
        </Link>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Body                                                                        */
/* -------------------------------------------------------------------------- */

function ResultsBody({
  results,
  channel,
  whatsapp,
  deliveryReports,
  projection,
  onShowSkipped,
}: {
  results: CampaignResults;
  channel: Channel;
  whatsapp: WaCampaignDelivery | null;
  deliveryReports: DeliveryReports | null;
  /** Slice 28. See `@wend/shared`'s `projection.ts` for the two gates. */
  projection: ProjectedFigures | null;
  onShowSkipped?: () => void;
}) {
  const reading = campaignResultsReading(results);
  const provider = CHANNEL_PROVIDER_LABEL[channel];
  const providerShort = CHANNEL_PROVIDER_SHORT_LABEL[channel];

  /*
   * ACCEPTED MEANS TWO DIFFERENT THINGS AND ONLY ONE OF THEM IS `reading`'s.
   *
   * `campaignResultsReading` computes `accepted` as `sent + delivered + bounced`
   * and deliberately leaves `failed` out, because on email a `failed` message is
   * one SES refused at the API call — it never entered the mail system and no
   * provider ever took it. On WhatsApp the same `messages.status` means the
   * opposite: the Graph API accepted the message, returned a `wamid`, and Meta
   * reported afterwards that it did not arrive. Every one of those was accepted.
   *
   * Reusing the shared arithmetic here would therefore under-report a WhatsApp
   * campaign by exactly its failures — and the campaign where that matters most
   * is the one where everything failed, which would render "0 accepted" beside a
   * full failure breakdown. So when the WhatsApp half is present its own
   * `accepted` and `delivered` are used, and the delivery rate is computed over
   * that denominator rather than over one that dropped the failures.
   *
   * `awaitingEvents`, `unresolved` and every credit figure below are NOT
   * re-derived: they are `messages.sent`, `messages.unknown` and the ledger,
   * which mean the same thing on both channels.
   */
  const accepted = whatsapp === null ? reading.accepted : whatsapp.accepted;
  const delivered = whatsapp === null ? results.messages.delivered : whatsapp.delivered;
  const deliveryRate = accepted === 0 ? 0 : delivered / accepted;

  if (accepted === 0) {
    /*
     * ZERO ACCEPTED IS NOT ONE FACT, AND IT IS NOT THREE EITHER. IT IS FOUR
     * INDEPENDENT COUNTS, AND A CHAINED TERNARY CAN ONLY EVER REPORT ONE.
     *
     * The version this replaces was itself a fix — one sentence became three,
     * gated on `failed` and `queued`, under a comment asserting there were
     * exactly three facts. It shipped two defects of the same family as the one
     * it cured, and both come from the same mistake: reading `accepted === 0`
     * as a *state* when it is an *arithmetic outcome* of counts that move
     * independently.
     *
     *   - **`unknown` was never read at all.** It is the fourth, and it is the
     *     one that contradicts the headline outright:
     *     `campaignResultsReading` computes `accepted` as `sent + delivered +
     *     bounced` and excludes `unknown` because *we cannot say whether the
     *     provider took it* — which is a reason to say so, not a licence to
     *     assert the opposite. A campaign whose every message the provider
     *     accepted and whose write then failed has `unknown` set and everything
     *     else zero, so it fell to the last arm and read "Nothing sent yet —
     *     Amazon SES has not accepted a message from this campaign". Four
     *     hundred messages were in real inboxes. The panel already owned the
     *     right words for it — the "N accepted but never confirmed" callout —
     *     and they sat in the `accepted > 0` return, unreachable in precisely
     *     the state that needed them.
     *   - **The refusal arm won over the queued arm.** `failed` and `queued`
     *     are independent counters and both are set on a campaign SES refused
     *     ten messages of and which then paused mid-run for credit — the runner
     *     leaves the rest of the rows `queued`, unreserved, on purpose. The
     *     reader was told the campaign was refused and finished, on a send that
     *     was still going to go out to real addresses.
     *
     * So nothing here is a ternary. Each count that is non-zero prints its own
     * line, the headline states only what is true of all of them, and adding a
     * fifth count means adding a fifth line rather than winning an argument
     * about which arm sits highest.
     *
     * **`failed` does not mean the same thing on the two channels, and the
     * honest answer is sometimes "we cannot tell from here".** On email it is a
     * pre-acceptance refusal: SES rejected it at the API call and it never
     * entered the mail system. On WhatsApp it is normally the opposite — the
     * Graph API took the message, returned a `wamid`, and Meta reported later
     * that it did not arrive — *except* that `readWhatsAppCampaignDelivery`
     * counts a WhatsApp `failed` row with no `sent_at` as a refusal too. When
     * the WhatsApp half is present, `whatsapp.accepted === 0` proves every
     * failure was pre-acceptance, so "refused" is exact. When it is absent —
     * the legacy response this component already gives a "not measured" branch
     * to — this panel genuinely cannot tell the two apart, and saying "WhatsApp
     * refused 40 messages and accepted none" about forty messages Meta accepted
     * and handed back ids for is the worst sentence on the panel.
     */
    const { queued, failed, unknown } = results.messages;
    const nothingHasHappened = queued === 0 && failed === 0 && unknown === 0;
    const failuresAreRefusals = channel === 'email' || whatsapp !== null;
    /*
     * And what a refusal *means* is channel-specific even where the word is
     * right. "Never entered the mail system" is exact for SES and is nonsense
     * about a Graph API call; the WhatsApp equivalent is that Meta handed back
     * no `wamid`, which is also the reason the message has no `sent_at` and so
     * never counted as accepted.
     */
    const refusalMeans =
      channel === 'whatsapp'
        ? 'never got a message id and never reached a phone'
        : 'never entered the mail system';

    return (
      <div className="flex flex-col gap-4">
        {nothingHasHappened ? (
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            Nothing sent yet — {provider} has not accepted a message from this campaign, so there is
            nothing to report. These are absences, not zeros.
          </p>
        ) : (
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            No delivery has been confirmed, so there is no rate to draw — these are absences, not
            zeros. What did happen to this campaign&rsquo;s messages is below.
          </p>
        )}

        {failed > 0 &&
          (failuresAreRefusals ? (
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
              {provider} refused {pluralise(failed, 'message')} outright, so{' '}
              {failed === 1 ? 'it' : 'they'} {refusalMeans}. Nothing was delivered, and a credit is
              consumed only on delivery.
            </p>
          ) : (
            <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
              {pluralise(failed, 'message')} recorded as failed. This response did not carry the
              WhatsApp delivery figures, and on WhatsApp the same status covers a message the Graph
              API refused and one it took and could not deliver — so this panel cannot say which
              these were. The recipient list says what happened to each one.
            </p>
          ))}

        {queued > 0 && (
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            {pluralise(queued, 'message')} still to be handed to {provider}. This campaign is still
            going out, so nothing here is final — do not schedule it a second time.
          </p>
        )}

        <AcceptedButNeverConfirmed count={unknown} channel={channel} />

        <NeverBecameAMessage skipped={results.recipients.skipped} onShow={onShowSkipped} />
      </div>
    );
  }

  /*
   * The delivered meter is drawn only when the caption under it is a rate —
   * the same three tests, in the same order. A bar at 0% over a campaign whose
   * events have not arrived would say "nothing delivered" in a shape nobody
   * reads past, which is the zero-versus-not-measured rule this panel exists to
   * keep; and below the significance floor the counts are exact and a bar would
   * draw a proportion the caption just refused to print.
   */
  const deliveredCaptionIsRate =
    !(delivered === 0 && reading.awaitingEvents === accepted) &&
    !(
      channel === 'email' &&
      onlyBouncesReported({
        delivered,
        bounced: results.messages.bounced,
        awaiting: reading.awaitingEvents,
      })
    ) &&
    reading.ratesAreSignificant;

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-2">
        {/*
         * ACCEPTED IS NOT DELIVERED, and the captions are where that is said
         * rather than left to the two numbers being different. They are the
         * first thing on the panel and a person reads them as one figure with a
         * qualifier unless told otherwise — "accepted" is a word that sounds
         * like an outcome. See the file header for the arithmetic behind each.
         */}
        <Figure
          label="Accepted"
          value={accepted}
          caption={
            channel === 'whatsapp'
              ? 'Taken by WhatsApp — not the same as delivered'
              : 'Handed to Amazon SES — not the same as delivered'
          }
        />
        {/*
         * AND THE DELIVERED CAPTION IS NOT ALWAYS A PERCENTAGE, BECAUSE THE
         * NUMBER UNDER IT IS NOT ALWAYS A MEASUREMENT.
         *
         * `deliveryRate` is `delivered / accepted` and it was rendered
         * unconditionally, so a campaign accepted an hour ago whose events have
         * not arrived read "Confirmed delivered — 0.0% of accepted" under a
         * zero. Nothing has failed. `reading.awaitingEvents` says so in the
         * paragraph below, and a caption is read before a paragraph is. That is
         * the zero-versus-not-measured rule at the top of the panel, where it
         * costs most.
         *
         * The middle case is the repo's other rate rule: below
         * `RATE_SIGNIFICANCE_MINIMUM` the counts are exact and the percentage is
         * not worth reading — "0.0%" of four accepted messages is one event away
         * from 25%. The bounce block a few sections down has said this since
         * slice 6; the figure a reader looks at first had not.
         */}
        <Figure
          label="Delivered"
          value={delivered}
          caption={
            delivered === 0 && reading.awaitingEvents === accepted
              ? `Confirmed delivered — none yet; ${providerShort} has reported no delivery for any of them`
              : channel === 'email' &&
                  onlyBouncesReported({
                    delivered,
                    bounced: results.messages.bounced,
                    awaiting: reading.awaitingEvents,
                  })
                ? `Confirmed delivered — none reported; ${providerShort} has reported bounces and not one delivery, so this is not a count of what arrived`
                : reading.ratesAreSignificant
                ? `Confirmed delivered — ${formatRate(deliveryRate)} of accepted`
                : `Confirmed delivered — ${formatCount(delivered)} of ${formatCount(accepted)} accepted, too few for a rate`
          }
          emphasis
          meter={deliveredCaptionIsRate ? deliveryRate : undefined}
        />
      </div>

      {delivered === 0 && reading.awaitingEvents === accepted ? (
        /*
         * NOTHING HAS COME BACK FOR THIS CAMPAIGN — the same test the Delivered
         * caption above uses. Whether that is "not yet" or "never, for any
         * campaign" is the workspace's fact, and it is said as one; see the
         * file header.
         */
        <UnreportedCampaign
          channel={channel}
          deliveryReports={deliveryReports}
          // Amazon's complaints are this campaign's reports too; Meta has none.
          complaints={channel === 'email' ? results.complaints : 0}
        />
      ) : (
        reading.awaitingEvents > 0 && (
          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            {pluralise(reading.awaitingEvents, 'message')} accepted with no{' '}
            {channel === 'whatsapp' ? 'delivery or failure' : 'delivery or bounce'} reported yet.{' '}
            {providerShort} has not answered for {reading.awaitingEvents === 1 ? 'it' : 'them'}, not
            lost {reading.awaitingEvents === 1 ? 'it' : 'them'}.
          </p>
        )
      )}

      {/*
        SLICE 28: THE PROJECTION, BELOW THE MEASURED FIGURES AND BELOW THE STRIP.

        Same component, same rank and same order as the Overview's: real counts,
        then the fact that nothing has been measured, then — clearly marked —
        what it would look like under a stated assumption. `projection` is null
        for every campaign on a workspace that has ever had a report of any kind,
        for every campaign with a terminal outcome of its own, and always on
        WhatsApp.
      */}
      {projection !== null && <ProjectedFigureRow projection={projection} />}

      <AcceptedButNeverConfirmed count={reading.unresolved} channel={channel} />

      <NeverBecameAMessage skipped={results.recipients.skipped} onShow={onShowSkipped} />

      {/*
       * BOUNCES ARE AN EMAIL CATEGORY AND THE GATE IS `isBounceMeasured`.
       *
       * Not `isDeliveryMeasured`, which since slice 12 is true on both channels
       * — that predicate was split in two precisely so this block could not
       * follow the delivered figure onto WhatsApp. Everything from here to the
       * end of `CampaignNotDelivered` is AWS's vocabulary and AWS's thresholds:
       * hard against soft, a complaint, the 5% an account is reviewed at. Meta
       * has no counterpart to any of it, so three zero counters and a rate
       * measured against an Amazon threshold would not be a quiet non-answer,
       * they would be arithmetic about a category the channel does not have.
       */}
      {isBounceMeasured(channel) ? (
        <SesFailureSections
          results={results}
          reading={reading}
          channel={channel}
          unreported={delivered === 0 && reading.awaitingEvents === accepted}
        />
      ) : whatsapp === null ? (
        /*
         * A WhatsApp campaign whose response carried no WhatsApp half.
         *
         * That is an API older than this screen, and it is NOT the same fact as
         * "no status has arrived yet" — which has its own shape,
         * `emptyWaCampaignDelivery`, and renders as zeros that were genuinely
         * looked for. Substituting one for the other here would report a
         * measurement that was never taken, which is the failure this whole
         * panel is organised around avoiding.
         */
        <div className="flex flex-col gap-3 pt-2">
          <Kicker rule>
            Delivery on WhatsApp
          </Kicker>
          <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
            Not measured, rather than nothing to report: this response did not carry the WhatsApp
            delivery figures at all. The accepted and delivered counts above come from the messages
            themselves and are unaffected.
          </p>
        </div>
      ) : (
        <CampaignWhatsAppDelivery delivery={whatsapp} />
      )}

      <CampaignCredits
        results={results}
        reading={reading}
        channel={channel}
        providerShort={providerShort}
      />

      {/* One fact, one line. The WhatsApp section prints its own "last status
          from WhatsApp" out of `lastStatusAt`, which is the same event read
          from the same table and named by the provider that sent it, so this
          generic line stands down rather than saying it a second time with a
          less specific label. */}
      {whatsapp === null && results.lastEventAt !== null && (
        <p className="font-mono text-[0.68rem] uppercase tracking-[0.1em] text-muted">
          Last event {formatDateTime(results.lastEventAt)}
        </p>
      )}
    </div>
  );
}

function CampaignCredits({
  results,
  reading,
  channel,
  providerShort,
}: {
  results: CampaignResults;
  reading: CampaignResultsReading;
  channel: Channel;
  providerShort: string;
}) {
  return (
    <div className="flex flex-col gap-3 pt-2">
      <Kicker rule>Credits</Kicker>

        <dl className="flex flex-wrap gap-2.5">
          <Counter label="Reserved" value={results.credits.reserved} />
          <Counter label="Consumed" value={results.credits.consumed} tone="text-text" />
          <Counter label="Released" value={results.credits.released} tone="text-good" />
        </dl>

        {/*
         * ONE RULE, TWO VOCABULARIES. Consume on delivery, release on the
         * failure — the same ledger rule on both channels, which is why this
         * section is shared rather than written twice. What differs is the name
         * of the failure: on email it is a bounce, a rejection or a rendering
         * failure; on WhatsApp there is no bounce at all and every non-delivery
         * is one word with a code beside it. A read consumes nothing — it is not
         * a second delivery, and the credit was settled when the message reached
         * the phone.
         */}
        <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
          A credit is consumed only on delivery — {pluralise(results.credits.consumed, 'credit')}{' '}
          here. {pluralise(results.credits.released, 'credit')} went back instead:{' '}
          {channel === 'whatsapp'
            ? 'a failure never consumes one, whatever WhatsApp gave as the reason, and a read receipt moves nothing either way.'
            : 'a bounce, a rejection or a rendering failure never consumes one.'}
        </p>

        {reading.creditsStillReserved > 0 && (
          <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
            {pluralise(reading.creditsStillReserved, 'credit')} still reserved for{' '}
            {pluralise(reading.awaitingEvents, 'message')} awaiting a final answer from{' '}
            {providerShort}.
          </p>
        )}

        {/*
         * "NIGHTLY" WAS WRONG, AND IT HAD BEEN WRONG SINCE SLICE 6.
         *
         * `credit-reconciliation.scheduler.ts` runs on `RECONCILE_INTERVAL_MS`,
         * which is one hour — its own header argues for the hour on the grounds
         * that a wrong balance is money being wrong. This callout told a tenant
         * looking at a disagreement about their credits that it would be checked
         * *tonight*, which is off by a factor of twelve in the direction that
         * makes somebody chase it themselves. The interval is deliberately not
         * printed: it is an implementation detail that can change, and "checks
         * the whole ledger regularly" is the part a reader can act on.
         */}
        {/*
          THE LEDGER-DISAGREEMENT CALLOUT WAS HERE, AND SLICE 28 REMOVED IT.

          The owner named it. It was an amber warning telling a marketer that two
          of Wend's internal counters disagree and that a background job will
          re-check them — on a screen where there is no action, no control and
          nothing to decide. A reader who cannot act on a warning learns to skip
          warnings, which is expensive on the day one of them matters.

          **The signal is not lost, and that is why this can go.**
          `CreditsService.reconcile()` runs on a schedule across tenants and
          reports discrepancies without repairing them — it has done since slice
          8, and it is the copy that reaches somebody who can act. What went is
          the marketer-facing early look at the same disagreement, which fired on
          every campaign whose reservations had not settled yet.

          `reading.creditsBalanceWithMessages` stays on the contract and stays
          asserted: it is the drift alarm, and the slice-27 projection had to be
          built as a sibling of `results` rather than a mutation of it precisely
          so this predicate could not start firing on every campaign.
        */}
    </div>
  );
}

/**
 * The one line that connects this panel's message counts to the people counts
 * above it — and the only thing this panel says about skipping.
 *
 * "Accepted: 1" under a campaign whose segment holds 526 reachable people is
 * the moment somebody concludes the product is broken, and the answer is one
 * panel up rather than in this one. So this states the gap and points at the
 * breakdown; it does not restate the reasons, because `CampaignProgressPanel`
 * owns them with the contract's own sentence for each, and two accounts of the
 * same fact on one screen are one account too many.
 *
 * Nothing renders when nobody was skipped: a "0 skipped" line is an answer to
 * a question nobody asked.
 */
function NeverBecameAMessage({ skipped, onShow }: { skipped: number; onShow?: () => void }) {
  if (skipped === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
      <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
        {pluralise(skipped, 'recipient')} never became a message, so{' '}
        {skipped === 1 ? 'it is' : 'they are'} in none of the figures here. Progress, above, breaks
        that down by reason.
      </p>
      {onShow !== undefined && (
        <Button type="button" variant="ghost" size="sm" onClick={onShow}>
          <ListFilter aria-hidden="true" className="size-4" />
          Show who was skipped
        </Button>
      )}
    </div>
  );
}

/**
 * "N accepted but never confirmed" — the `unknown` messages, in both branches.
 *
 * It is a component rather than the inline `reading.unresolved > 0 && …` it used
 * to be for one reason: it lived inside the `accepted > 0` return, and `unknown`
 * is deliberately excluded from `accepted`, so the callout was unreachable in
 * exactly the state it exists for — every message of a campaign accepted by the
 * provider and never recorded. The panel then said "Nothing sent yet" about
 * them. Two call sites and one implementation is what stops the two answers
 * drifting into a third disagreement.
 *
 * Zero renders nothing. A "0 accepted but never confirmed" callout would be an
 * alarm about a state the campaign is not in.
 */
function AcceptedButNeverConfirmed({ count, channel }: { count: number; channel: Channel }) {
  if (count === 0) return null;

  /*
   * SLICE 28: THE COUNT STAYS, THE POST-MORTEM GOES.
   *
   * `messageStatusMeaning('unknown', channel)` describes a database write that
   * failed after the provider had already accepted the message, and the decision
   * not to retry it. Both are true and neither is this reader's: a sender's
   * whole take-away is that some people may or may not have got it, and the
   * number of them. Why the row is missing, and the no-retry policy, are
   * operator facts — they stay in the contract, where the ingest and the
   * runner read them, and off this screen.
   */
  return (
    <Callout tone="info" title={`${formatCount(count)} accepted but never confirmed`}>
      {channel === 'whatsapp' ? 'Meta' : 'Amazon'} took{' '}
      {count === 1 ? 'this one' : 'these'} and Wend never recorded what happened next, so{' '}
      {count === 1 ? 'it' : 'they'} may or may not have arrived.
    </Callout>
  );
}

/**
 * One headline figure — Accepted or Delivered — as a `MetricPanel`.
 *
 * Slice 66 moved these off a 2.1rem mono numeral painted in the brand gradient
 * and onto the shared figure panel: sans, tabular digits (mono's dotted zero
 * reads as an 8), the kicker role, and the caption as the panel's `sub` — the
 * caption is still the sentence that says accepted is not delivered, and it is
 * still under the number rather than behind a tooltip. `emphasis` is the one
 * figure a reader should leave with and gets the violet panel; the other is the
 * plain card skin. The prop is still called `label`, because a suite reads
 * `label="Accepted"` and `label="Delivered"` from this file to prove the two
 * are still two figures.
 *
 * `meter` is a fraction drawn under the figure — magnitude only, never a tone —
 * and the caller passes it only when the caption is itself a rate.
 */
function Figure({
  label,
  value,
  caption,
  emphasis = false,
  meter,
}: {
  label: string;
  value: number;
  caption: string;
  emphasis?: boolean;
  meter?: number | undefined;
}) {
  return (
    <MetricPanel
      kicker={label}
      figure={{ kind: 'measured', value }}
      figureSize="lg"
      sub={caption}
      variant={emphasis ? 'brand' : 'plain'}
      {...(meter === undefined ? {} : { meter: { value: meter } })}
    />
  );
}
